import { booleanAttribute, Component, computed, input, model, output } from "@angular/core";

import { TimelineBar } from "../timeline-bar/timeline-bar.component";
import { TimelineChip } from "../timeline-chip/timeline-chip.component";
import { TimelineNav } from "../timeline-nav/timeline-nav.component";
import {
  addDays,
  addMonths,
  DayKey,
  formatDayLong,
  formatMonth,
  mondayOf,
  monthEnd,
  monthStart,
  parseDay,
} from "../timeline-dates";
import { BarView, chipsOn, layoutBars } from "../timeline-layout";
import { TimelineEntry, TimelineRange } from "../timeline.model";

interface CalendarDay {
  day: DayKey;
  column: number;
  date: number;
  inMonth: boolean;
  outOfRange: boolean;
  today: boolean;
  chips: TimelineEntry[];
  more: number;
  /** Phone month: colored dots for everything on the day, bars included. */
  dots: string[];
  dayLabel: string;
}

interface CalendarWeek {
  start: DayKey;
  days: CalendarDay[];
  bars: BarView[];
  gridRows: string;
  chipRow: number;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** Month grid, Monday first. Days outside the loaded range are grayed out and empty. */
@Component({
  selector: "lib-timeline-calendar",
  templateUrl: "./timeline-calendar.component.html",
  styleUrl: "./timeline-calendar.component.scss",
  imports: [TimelineBar, TimelineChip, TimelineNav],
  host: { "[class.dots-only]": "dotsOnly()", "[class.fill]": "fill()" },
})
export class TimelineCalendar {
  readonly entries = input.required<TimelineEntry[]>();
  /** First day of the month shown. */
  readonly month = model.required<DayKey>();
  readonly range = input.required<TimelineRange>();
  readonly today = input.required<DayKey>();
  readonly selectedId = input<string | null>(null);
  readonly selectedDay = input<DayKey | null>(null);
  readonly maxChips = input(4);
  /** Phone: day numbers with dots; tapping a day lists it below. */
  readonly dotsOnly = input(false, { transform: booleanAttribute });
  /** Accessible name of the calendar, e.g. "Project Calendar". */
  readonly label = input("Calendar");
  /** Off for an unattended view such as a wallboard. */
  readonly navigation = input(true);
  /** Fill the parent's height and scroll inside it with no scrollbar, keeping the nav bar and weekday row in view. */
  readonly fill = input(false);
  readonly entrySelected = output<string>();
  readonly daySelected = output<DayKey>();

  protected readonly weekdays = WEEKDAYS;

  /** Today / Prev / Next show when the range spans more than one month. */
  protected readonly canPage = computed(
    () => this.navigation() && monthStart(this.range().start) < monthStart(this.range().end)
  );
  protected readonly navTitle = computed(() => formatMonth(this.month()));
  protected readonly atFirst = computed(() => this.month() <= monthStart(this.range().start));
  protected readonly atLast = computed(() => monthEnd(this.month()) >= this.range().end);

  protected step(direction: 1 | -1): void {
    this.month.set(addMonths(this.month(), direction));
  }

  protected goToday(): void {
    this.month.set(monthStart(this.today()));
  }

  protected readonly weeks = computed<CalendarWeek[]>(() => {
    const month = this.month();
    const last = monthEnd(month);
    const range = this.range();
    const entries = this.entries();
    const weeks: CalendarWeek[] = [];
    for (let start = mondayOf(month); start <= last; start = addDays(start, 7)) {
      const inRange = entries.filter((entry) => entry.start <= range.end && entry.end >= range.start);
      const { bars, lanes } = layoutBars(inRange, start, 7);
      const chipRow = lanes + 2;
      const days = Array.from({ length: 7 }, (_, i): CalendarDay => {
        const day = addDays(start, i);
        const outOfRange = day < range.start || day > range.end;
        const chips = outOfRange ? [] : chipsOn(entries, day);
        const barsToday = outOfRange
          ? []
          : entries.filter((entry) => entry.bar && entry.start <= day && entry.end >= day);
        const count = chips.length + barsToday.length;
        return {
          day,
          column: i + 1,
          date: parseDay(day).getDate(),
          inMonth: day.slice(0, 7) === month.slice(0, 7),
          outOfRange,
          today: day === this.today(),
          chips: chips.slice(0, this.maxChips()),
          more: Math.max(0, chips.length - this.maxChips()),
          dots: [...chips, ...barsToday].slice(0, 4).map((entry) => entry.color),
          dayLabel: `${formatDayLong(day)}${outOfRange ? ", outside the timeline range" : count ? `, ${count} ${count === 1 ? "entry" : "entries"}` : ", no entries"}`,
        };
      });
      weeks.push({
        start,
        days,
        bars,
        chipRow,
        gridRows: `30px ${lanes ? `repeat(${lanes}, 22px) ` : ""}minmax(84px, auto)`,
      });
    }
    return weeks;
  });
}
